// Pure presentation helpers for the GrowthOS Alloy page. Nothing here renders or calls the API, so every
// sentence and number the page shows can be tested without mounting it.

import { money } from "@/components/sessions/sessionData";

export function formatUsd(value) {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `$${amount.toFixed(2)}`;
}

// Alloy tokens keep up to five decimal places (Austin, 2026-11-03): at 5 tokens to the dollar a cheap request is a
// fraction of a token, and rounding it to a whole token hid it.
export const TOKEN_DECIMALS = 5;
const TOKEN_SCALE = 10 ** TOKEN_DECIMALS;

/** 12,150 · 483.3 · 0.257: Alloy tokens with thousands separators and up to five decimals, trailing zeros dropped. */
export function formatTokens(value) {
  const amount = Number(value);
  return (Number.isFinite(amount) ? amount : 0).toLocaleString("en-US", { maximumFractionDigits: TOKEN_DECIMALS });
}

/** Retail dollars as tokens at the summary's ratio, to five decimals; anything unreadable is 0. */
export function tokensFromUsd(usd, perUsd) {
  const amount = Number(usd);
  const ratio = Number(perUsd);
  return Number.isFinite(amount) && Number.isFinite(ratio) ? Math.round(amount * ratio * TOKEN_SCALE) / TOKEN_SCALE : 0;
}

/**
 * How the page prints credit amounts: Alloy tokens when the summary carries a tokens block (the credits function
 * sends one for the brands on tokens, AXOS first), dollars for everyone else. The dollar fields are what the
 * function sends either way; the block carries the ratio, so every figure converts here with one rounding rule.
 *   amount(usd)      a retail-dollar figure as a bare number: "483.3" or "$96.66"
 *   labelled(usd)    the same with its unit word: "483.3 tokens" or "$96.66"
 *   fromCents(cents) a ledger amount (a package, a top-up): "250" or "$50.00"
 *   recorded(usd)    a recorded cost the usage-by-person panel prints: a dash stays a dash, a trace below five decimals
 *                    reads "<0.00001"
 *   cost(usd)        a recorded cost with its unit word, for tables beside model-token counts: "0.257 tokens" or "$0.0514"
 */
export function creditUnit(summary = {}) {
  const block = summary?.tokens;
  const perUsd = Number(block?.perUsd);
  if (block && typeof block === "object" && Number.isFinite(perUsd) && perUsd > 0) {
    const amount = (usd) => formatTokens(tokensFromUsd(usd, perUsd));
    const recorded = (usd) => (usd == null ? "—" : Number(usd) > 0 && tokensFromUsd(usd, perUsd) === 0 ? `<${formatTokens(1 / TOKEN_SCALE)}` : amount(usd));
    return {
      tokens: true,
      perUsd,
      noun: "tokens",
      name: "Alloy tokens",
      amount,
      labelled: (usd) => `${amount(usd)} tokens`,
      fromCents: (cents) => formatTokens(tokensFromUsd((Number(cents) || 0) / 100, perUsd)),
      recorded,
      cost: (usd) => {
        const value = recorded(usd);
        return value === "—" ? value : `${value} ${value === "1" ? "token" : "tokens"}`;
      },
    };
  }
  return {
    tokens: false,
    perUsd: null,
    noun: "credits",
    name: "Alloy credits",
    amount: formatUsd,
    labelled: formatUsd,
    fromCents: (cents) => formatUsd((Number(cents) || 0) / 100),
    recorded: money,
    cost: money,
  };
}

/** 1,572 · 25.6K · 154.8M: a count a brand owner can read at a glance. */
export function formatCount(value) {
  const count = Math.max(0, Number(value) || 0);
  if (count < 10_000) return Math.round(count).toLocaleString("en-US");
  if (count < 1_000_000) return `${trimZero((count / 1_000).toFixed(1))}K`;
  if (count < 1_000_000_000) return `${trimZero((count / 1_000_000).toFixed(1))}M`;
  return `${trimZero((count / 1_000_000_000).toFixed(1))}B`;
}

function trimZero(text) {
  return text.endsWith(".0") ? text.slice(0, -2) : text;
}

/**
 * What the ON/OFF switch can do right now. A manager can always flip an OFF switch in a workspace that is set up:
 * with credits it turns Alloy on, without credits it opens payment first and Alloy turns on after checkout.
 */
export function creditsPresentation(summary = {}) {
  const route = summary.route && ["queued", "applying"].includes(summary.route.status) ? summary.route : null;
  if (route) {
    const on = route.requestedState === "on";
    return { state: on ? "turning-on" : "turning-off", label: on ? "Turning on…" : "Turning off…", pending: true, on, canToggle: false, needsPayment: false, canAddCredits: true, blocker: null };
  }
  const on = summary.alloyState === "on";
  const label = on ? "Alloy is on" : "Alloy is off";
  const balance = Number(summary.balanceUsd || 0);
  if (summary.configured === false) {
    return { state: on ? "on" : "off", label, pending: false, on, canToggle: on, needsPayment: false, canAddCredits: false, blocker: "Alloy isn't set up for this workspace yet." };
  }
  if (!summary.keyReady) {
    return { state: on ? "on" : "off", label, pending: false, on, canToggle: on, needsPayment: false, canAddCredits: true, blocker: "Your agent's Alloy key is being created. Tokens apply automatically once it is ready." };
  }
  if (on) return { state: "on", label, pending: false, on, canToggle: true, needsPayment: false, canAddCredits: true, blocker: null };
  return { state: "off", label, pending: false, on, canToggle: true, needsPayment: balance <= 0, canAddCredits: true, blocker: null };
}

// Every level the route accepts. Anything else the backend ever reports still shows, rather
// than being silently misrepresented by a control that cannot display it.
export const EFFORT_CHOICES = ["none", "low", "medium", "high", "xhigh"];
export function effortOptions(model) {
  const current = model?.reasoningEffort || "medium";
  return EFFORT_CHOICES.includes(current) ? EFFORT_CHOICES : [...EFFORT_CHOICES, current];
}

// Two spellings: one reads as a sentence after the model, the other stands alone in the picker.
const EFFORT_LABELS = Object.freeze({ none: "No", low: "Low", medium: "Medium", high: "High", xhigh: "Extra High" });
const EFFORT_OPTION_LABELS = Object.freeze({ none: "None", low: "Low", medium: "Medium", high: "High", xhigh: "Extra high" });
export function effortLabel(effort) {
  const key = String(effort || "medium");
  return EFFORT_LABELS[key] || key;
}
export function effortOptionLabel(effort) {
  const key = String(effort || "medium");
  return EFFORT_OPTION_LABELS[key] || key;
}

export function modelLabel(model) {
  const name = !model || !model.overridden ? "Auto" : model.model;
  return `${name} - ${effortLabel(model?.reasoningEffort)} Reasoning`;
}

/** Who changed the model and when, kept separate so the model line itself stays short. */
export function modelChangeNote(model) {
  if (!model?.overridden) return "";
  const raw = typeof model.chosenBy === "string" ? model.chosenBy : "";
  const who = raw.startsWith("agent:") ? "your team in Slack" : (raw || "your team");
  const when = model.chosenAt ? ` on ${new Date(model.chosenAt).toLocaleDateString()}` : "";
  return `Changed by ${who}${when}.`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Days the balance lasts at this calendar month's pace. The credits summary reports usage for the current UTC
 * day, Monday-to-Sunday week and calendar month, so the pace is the month's spend over the days elapsed in it.
 */
export function runwayDays(summary, now = new Date()) {
  const balance = Number(summary?.balanceUsd || 0);
  const monthUsd = Number(summary?.usage?.monthlyUsd || 0);
  if (!(balance > 0) || !(monthUsd > 0)) return null;
  const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  const elapsedDays = Math.max(1, (now.getTime() - monthStart) / DAY_MS);
  return balance / (monthUsd / elapsedDays);
}

/** The card's runway line: time, not money. */
export function runwayLine(summary, now = new Date()) {
  const view = creditsPresentation(summary);
  const balance = Number(summary?.balanceUsd || 0);
  if (!view.on && !view.pending) return balance > 0 ? "Ready when you turn Alloy on" : "Turn Alloy on to get started";
  const days = runwayDays(summary, now);
  if (days === null) return balance > 0 ? "No usage yet this month" : "";
  if (days < 1) return "Less than a day left at this month's pace";
  // A brand that has barely used Alloy this month would otherwise read "≈ 521 months left".
  if (days >= 365) return "More than a year left at this month's pace";
  if (days >= 60) return `≈ ${Math.floor(days / 30)} months left at this month's pace`;
  const whole = Math.floor(days);
  return `≈ ${whole} ${whole === 1 ? "day" : "days"} left at this month's pace`;
}

const TIER_ORDER = ["premium", "standard", "fast"];

/**
 * The routing panel, from the summary's `routing` block (the last 30 completed days on the agent's key).
 * A saving is only claimed when there is a positive one to report. It reads in dollars on either unit: on Austin's
 * call (2026-10-30) the saving is the one figure a brand on Alloy tokens still sees as money.
 */
export function routingPresentation(routing, summary = {}) {
  const model = summary?.model;
  if (!routing || typeof routing !== "object") {
    // The credits function sends no routing block for a brand that has no Alloy key yet; that is not a failure.
    const setUp = summary?.configured !== false && Boolean(summary?.keyReady);
    return { state: setUp ? "unavailable" : "empty" };
  }
  const requests = Math.max(0, Number(routing.requests) || 0);
  if (requests === 0) return { state: "empty" };
  const comparator = String(routing.comparator?.label || "a premium model");
  const savedUsd = Number(routing.savedUsd);
  const savedPercent = Number(routing.savedPercent);
  const saving = Number.isFinite(savedUsd) && savedUsd >= 0.01 && Number.isFinite(savedPercent) && savedPercent >= 1;
  const mix = TIER_ORDER.map((tier) => ({ tier, percent: Math.max(0, Math.round(Number(routing.mix?.[tier]) || 0)) }));
  return {
    state: "ready",
    saving,
    headline: saving ? `Saved ≈ ${formatUsd(savedUsd)}` : `${formatCount(requests)} tasks handled`,
    comparison: saving ? `vs. running every task on ${comparator}` : "",
    method: saving ? `Estimated from ${formatCount(requests)} tasks, repriced at ${comparator}'s list price.` : "",
    note: model?.overridden ? "Your team chose a specific model for this agent." : "Auto picks the right model for each task.",
    mix,
    hasMix: mix.some((entry) => entry.percent > 0),
    stats: [
      { label: "Tasks", value: formatCount(requests) },
      // "Model tokens", never just "tokens": Alloy tokens are the balance, model tokens are the text the models read and wrote.
      { label: "Model tokens", value: formatCount(routing.tokens) },
      { label: `Below ${comparator}`, value: saving ? `${savedPercent}%` : "—" },
    ],
  };
}

const PURCHASE_KIND_LABELS = Object.freeze({
  starter: "Token purchase",
  topup: "Token purchase",
  auto_recharge: "Auto-recharge",
  migration: "Tokens carried over",
  adjustment: "Token adjustment",
  reward: "Reward",
});

export function purchaseLabel(kind) {
  const key = String(kind || "");
  if (PURCHASE_KIND_LABELS[key]) return PURCHASE_KIND_LABELS[key];
  const words = key.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Tokens";
}

export const ACTION_ERROR_SENTENCES = Object.freeze({
  credits_model_unknown: "That model isn't available.",
  credits_model_invalid: "That isn't a valid model id, which looks like provider/model.",
  credits_model_locked_while_on: "Turn Alloy off before choosing a specific model. Reasoning effort can be changed any time.",
  credits_reasoning_effort_invalid: "Reasoning effort must be none, low, medium, high or xhigh.",
  credits_route_in_progress: "A switch is already in progress. Give it a minute, then refresh.",
  credits_balance_required: "Your tokens are still being applied. Try again in a minute.",
  credits_payment_method_required: "Buy tokens once with a card first; auto-recharge uses that card.",
  credits_key_missing: "Your agent's Alloy key isn't ready yet. Try again in a few minutes.",
  credits_unavailable: "Alloy tokens are temporarily unavailable. Please try again shortly.",
});
export const GENERIC_ACTION_ERROR = "That didn't work. Please try again.";

export function actionErrorSentence(error) {
  const code = error?.code;
  if (code) return ACTION_ERROR_SENTENCES[code] || GENERIC_ACTION_ERROR;
  return error?.message || GENERIC_ACTION_ERROR;
}

// ---- the Stripe round trip -----------------------------------------------------------------------------------
// Every checkout this tab starts is remembered in session storage: which workspace paid, and whether the buyer flipped
// the switch to get there. Stripe returns to /alloy?checkout=success|cancelled with nothing else, so this record is
// what ties the return to a workspace, and a paid "turn Alloy on" keeps being honoured across reloads for a while.

export const CHECKOUT_KEY = "growthos.alloy.checkout";
export const CHECKOUT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // a Stripe Checkout session lives a day
export const TURN_ON_AFTER_PAYMENT_MS = 15 * 60 * 1000;
export const TURN_ON_WAIT_MS = 5 * 60 * 1000;

function safeSessionStorage() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function rememberCheckout(clientSlug, { turnOn = false } = {}, now = Date.now(), storage = safeSessionStorage()) {
  if (!storage || !clientSlug) return;
  try {
    storage.setItem(CHECKOUT_KEY, JSON.stringify({ clientSlug, turnOn: Boolean(turnOn), at: now, paidAt: null }));
  } catch {
    // Blocked storage only loses the automatic turn-on; the payment itself still goes through.
  }
}

export function readCheckout(now = Date.now(), storage = safeSessionStorage()) {
  if (!storage) return null;
  try {
    const record = JSON.parse(storage.getItem(CHECKOUT_KEY) || "null");
    if (!record || typeof record.clientSlug !== "string" || !Number.isFinite(record.at)) return null;
    if (now - record.at < 0 || now - record.at > CHECKOUT_MAX_AGE_MS) return null;
    return record;
  } catch {
    return null;
  }
}

/** Stripe said the payment went through: stamp it, for the workspace that started the checkout only. */
export function markCheckoutPaid(clientSlug, now = Date.now(), storage = safeSessionStorage()) {
  const record = readCheckout(now, storage);
  if (!record || record.clientSlug !== clientSlug) return null;
  const paid = { ...record, paidAt: now };
  try {
    storage.setItem(CHECKOUT_KEY, JSON.stringify(paid));
  } catch {
    // the notice still shows; only a reload loses the automatic turn-on
  }
  return paid;
}

export function forgetCheckout(storage = safeSessionStorage()) {
  try {
    storage?.removeItem(CHECKOUT_KEY);
  } catch {
    // nothing to clear
  }
}

/** A paid "turn Alloy on" for this workspace that is still fresh: when it was paid, or null. */
export function pendingTurnOn(clientSlug, now = Date.now(), storage = safeSessionStorage()) {
  const record = readCheckout(now, storage);
  if (!record || record.clientSlug !== clientSlug || !record.turnOn || !Number.isFinite(record.paidAt)) return null;
  if (now - record.paidAt < 0 || now - record.paidAt > TURN_ON_AFTER_PAYMENT_MS) return null;
  return { paidAt: record.paidAt };
}

/**
 * The next step while waiting to turn Alloy on after a payment: done once it is on or switching on, request ON as
 * soon as the credits land, give up after a few minutes without guessing.
 */
export function turnOnStep(summary, startedAt, now = Date.now()) {
  if (!summary) return "wait";
  const route = summary.route && ["queued", "applying"].includes(summary.route.status) ? summary.route : null;
  if (summary.alloyState === "on" || route?.requestedState === "on") return "done";
  if (route) return "wait";
  if (summary.configured !== false && summary.keyReady && Number(summary.balanceUsd || 0) > 0) return "request";
  if (now - startedAt > TURN_ON_WAIT_MS) return "give-up";
  return "wait";
}
